import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { isProfessional } from '../lib/auth/authService';
import { SubscriptionModal } from './Subscription/SubscriptionModal';

interface HeroProps {
  title: string;
  subtitle: string;
  image: string;
  showNearMe?: boolean;
  showAddListing?: boolean;
  onAddListing?: () => void;
  addListingButtonText?: string;
  addListingButtonIcon?: React.ReactNode;
}

export function Hero({ title, subtitle, image, showNearMe = false, showAddListing = false, onAddListing, addListingButtonText = 'Submit Listing', addListingButtonIcon }: HeroProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [canCreateListing, setCanCreateListing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    // Check authentication status
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session) {
        checkAccess();
      } else {
        setCheckingAccess(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        checkAccess();
      } else {
        setCanCreateListing(false);
        setCheckingAccess(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAccess = async () => {
    setCheckingAccess(true);
    const hasProfessionalAccess = await isProfessional();
    setCanCreateListing(hasProfessionalAccess);
    setCheckingAccess(false);
  };

  const handleAddListing = () => {
    if (checkingAccess) return;
    if (!isAuthenticated) {
      sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else if (canCreateListing) {
      onAddListing?.();
    } else {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div className="relative mb-12 bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white overflow-hidden">
      {/* Mobile Hero Image with rounded borders and margin */}
      <div className="lg:hidden w-full px-4 mt-4 mb-8">
        <img
          src={image}
          alt="Hero"
          className="w-full h-[160px] object-cover blob-shape"
        />
      </div>

      <div className="py-8 lg:py-16 relative z-10">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              {showNearMe && (
                <button className="mb-6 px-6 py-2 bg-[#FFC107] text-white rounded-full font-medium hover:bg-[#FFA000] transition-colors whitespace-nowrap">
                  Near Me
                </button>
              )}
              <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-4 lg:mb-6">{title}</h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8">{subtitle}</p>
              {showAddListing && (
                <button 
                  onClick={handleAddListing}
                  disabled={checkingAccess}
                  className="inline-flex items-center justify-center gap-2 px-6 lg:px-10 py-3 bg-[#8DA847] text-white rounded-full font-medium hover:bg-[#7A9339] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingAccess ? (
                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {addListingButtonIcon}
                      {addListingButtonText}
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="hidden lg:block">
              <img 
                src={image} 
                alt="Hero" 
                className="w-full h-[450px] object-cover blob-shape"
              />
            </div>
          </div>
        </div>
      </div>
      {showUpgradeModal && (
        <SubscriptionModal
          onClose={() => setShowUpgradeModal(false)}
          onSuccess={() => {
            setShowUpgradeModal(false);
            checkAccess();
          }}
        />
      )}
    </div>
  );
}